import {
  Database,
  DatabaseEntry,
  DatabaseField,
  DatabaseFieldType,
  ImportedFileType,
} from '@databases/types/database';
import {
  createEntry,
  createField,
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  getExistingFieldNames,
  getUniqueFieldName,
} from '@databases/utils/database/utils';

import {
  ImportEntryMode,
  ImportEntryReference,
  ImportFieldMode,
  ImportFieldReference,
  ImportResult,
  ImportSettings,
} from './types';

/**
 * The supported import MIME types.
 */
export const importMimeTypes: Record<string, ImportedFileType> = {
  'text/html': ImportedFileType.HTML,
  'text/csv': ImportedFileType.CSV,
  'text/plain': ImportedFileType.CSV, // CSV file type for macOS
  'text/tab-separated-values': ImportedFileType.CSV,
  'application/vnd.ms-excel': ImportedFileType.CSV, // CSV file type for Windows
};

/**
 * Returns true if the imported file is empty or if there is no item in the clipboard for paste.
 */
export function isImportedContentEmpty(files: File[] | DataTransferItemList): boolean {
  return files.length === 0 || (Array.isArray(files) && files[0].size === 0);
}

export function resolveFieldReference(
  database: Database,
  fieldRef: ImportFieldReference | undefined
): DatabaseField | undefined {
  if (fieldRef?.type === ImportFieldMode.EXISTING) {
    return getDatabaseFieldOptional(database, fieldRef.fieldId);
  } else if (fieldRef?.type === ImportFieldMode.CREATE) {
    return fieldRef.field;
  }
}

export function resolveEntryReference(
  database: Database,
  entryRef: ImportEntryReference | undefined
): DatabaseEntry | undefined {
  if (entryRef?.type === ImportEntryMode.EXISTING) {
    return getDatabaseEntryOptional(database, entryRef.entryId);
  } else if (entryRef?.type === ImportEntryMode.CREATE) {
    return entryRef.entry;
  }
}

/**
 * Determines the missing rows or columns based on the given all rows, selected rows and imported rows.
 * @param allItems - All rows or columns
 * @param selectedItems - Selected rows or columns
 * @param importedItems - Imported rows or columns
 * @returns the last 'n' entries of importedRows, where 'n' is the difference between the sum of the index and the length of importedRows,
 * and the length of allRows. Otherwise, returns an empty array.
 */
export const getMissingItems = <A>(
  allItems: string[],
  selectedItems: string[],
  importedItems: Array<A>
) => {
  const selectedIndex = allItems.indexOf(selectedItems[0]);

  const itemsToReturn = selectedIndex + importedItems.length - allItems.length;

  if (itemsToReturn > 0) {
    // Return the last items of the importedItems
    return importedItems.slice(-itemsToReturn);
  } else {
    return [];
  }
};

/**
 * Determines the import settings which contain entry references and field references with ImportEntryMode.EXISTING taking the
 * fieldIds and entryIds as input and starting from the selected indexes until the end of the fieldIds and entryIds.
 * @returns the import settings for the selected field and entry ids.
 */
export const getImportSettingsFromSelection = (
  selectedFieldIds: string[],
  selectedEntryIds: string[],
  fieldIds: string[],
  entryIds: string[]
): ImportSettings => ({
  fieldReferences: fieldIds
    .slice(fieldIds.indexOf(selectedFieldIds[0]))
    .map((fieldId) => ({
      type: ImportFieldMode.EXISTING,
      fieldId,
    })),
  entryReferences: entryIds
    .slice(entryIds.indexOf(selectedEntryIds[0]))
    .map((entryId) => ({
      type: ImportEntryMode.EXISTING,
      entryId,
    })),
  selectedRows: selectedEntryIds.map(() => true),
  selectedColumns: selectedFieldIds.map(() => true),
});

/**
 * Determines the import settings which contain entry references and field references with ImportEntryMode.CREATE and
 * ImportFieldMode.CREATE. Takes the settings for the selection and appends the missing fields and entries to the fieldReferences and
 * entryReferences. New fields are created with a unique name.
 * @returns the import settings for the selected field and entry ids as well as the missing fields and entries.
 */
export const getImportSettingsWithMissingItems = (
  selectedFieldIds: string[],
  selectedEntryIds: string[],
  fieldIds: string[],
  entryIds: string[],
  database: Database,
  importResult: ImportResult,
  fieldLabel: string
): ImportSettings => {
  const settings = getImportSettingsFromSelection(
    selectedFieldIds,
    selectedEntryIds,
    fieldIds,
    entryIds
  );
  const missingEntries = getMissingItems(entryIds, selectedEntryIds, importResult.rows);
  const missingFields = getMissingItems(
    fieldIds,
    selectedFieldIds,
    importResult.rows[0].cols
  );
  const addedFieldNames: string[] = [];
  return {
    ...settings,
    fieldReferences: [
      ...settings.fieldReferences,
      ...missingFields.map(() => {
        const uniqueFieldName = getUniqueFieldName(fieldLabel, [
          ...getExistingFieldNames(database),
          ...addedFieldNames,
        ]);
        addedFieldNames.push(uniqueFieldName);
        return {
          type: ImportEntryMode.CREATE,
          field: {
            ...createField(DatabaseFieldType.TEXT),
            name: uniqueFieldName,
          },
        };
      }),
    ] as ImportFieldReference[],
    entryReferences: [
      ...settings.entryReferences,
      ...missingEntries.map(() => ({
        type: ImportFieldMode.CREATE,
        entry: createEntry(database),
      })),
    ] as ImportEntryReference[],
  };
};

/**
 * An unreachable case that helps ensure the capture of uncovered cases
 * by explicitly making typescript error when used in conjunction
 * with a switch statement.
 */
export const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};
